import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { BookService } from '../../services/BookService';
import { BooksDTO } from '../../dto/BooksDTO';
import { useAppContext } from '../../AppContext';
import { useTranslation } from 'react-i18next';

const Breadcrumb = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pathnames = location.pathname.split('/').filter((x) => x);
  const [bookName, setBookName] = useState<string>('');
  const [navigationHistory, setNavigationHistory] = useState<string[]>([]);
  const [bookNames, setBookNames] = useState<{ [key: string]: string }>({});
  const { role } = useAppContext();
  const { t } = useTranslation();

  useEffect(() => {
    // Update navigation history when location changes
    setNavigationHistory(prev => {
      const newPath = location.pathname;
      if (prev[prev.length - 1] !== newPath) {
        // If navigating from one book to another book, replace the last book path
        if (newPath.includes('/book/') && prev.length > 0 && prev[prev.length - 1].includes('/book/')) {
          // Replace the last book path with the new one
          return [...prev.slice(0, -1), newPath];
        }
        // If navigating to a book page from book store
        if (newPath.includes('/book/') && prev[prev.length - 1] === '/book-store') {
          return [...prev, newPath];
        }
        // If navigating to a non-book page, clear the history
        if (!newPath.includes('/book/')) {
          return [newPath];
        }
        return [...prev, newPath];
      }
      return prev;
    });

    // Clear book names when navigating to non-book pages
    if (!location.pathname.includes('/book/')) {
      setBookNames({});
    }
  }, [location]);

  useEffect(() => {
    console.log('navigationHistory', navigationHistory);
  }, [navigationHistory]);

  useEffect(() => {
    console.log(navigationHistory, bookNames);
    const fetchBookNames = async () => {
      const newBookNames = { ...bookNames };
      let hasChanges = false;

      // Get all book IDs from navigation history
      const bookIds = navigationHistory
        .filter(path => path.includes('/book/'))
        .map(path => path.split('/book/')[1]);

      // Fetch names for any new books
      for (const bookId of bookIds) {
        if (!bookNames[bookId]) {
          try {
            const response = await BookService.GetBookById(bookId);
            const book = response as BooksDTO;
            newBookNames[bookId] = book.bookTitle;
            hasChanges = true;
          } catch (error) {
            console.error('Error fetching book name:', error);
          }
        }
      }

      if (hasChanges) {
        setBookNames(newBookNames);
      }
    };

    fetchBookNames();
  }, [navigationHistory]);

  useEffect(() => {
    const fetchBookName = async () => {
      if (pathnames[0] === 'book' && pathnames[1]) {
        try {
          const response = await BookService.GetBookById(pathnames[1]);
          const book = response as BooksDTO;
          setBookName(book.bookTitle);
        } catch (error) {
          setBookName(t('bookDetails.details'));
        }
      }
    };

    fetchBookName();
  }, [pathnames, t]);

  const handleBookClick = (bookId: string, index: number) => {
    // Keep only the history up to the clicked book
    const newHistory = navigationHistory.slice(0, index + 1);
    setNavigationHistory(newHistory);
  };

  // Don't show breadcrumb on home pages
  if (location.pathname === '/' || location.pathname === '/home') {
    return null;
  }

  // Special case for book details - show complete navigation path
  if (pathnames[0] === 'book' && bookName) {
    // Get only the book paths from navigation history
    const bookPaths = navigationHistory.filter(path => path.includes('/book/'));
    const showBookStore = navigationHistory.includes('/book-store') && role !== 'publisher';
    
    return (
      <nav aria-label="breadcrumb" className="bg-[rgba(73,156,151,0.24)] border-b-[1px] border-[rgba(73,156,151,0.24)]">
        <div className="px-[32px] md:px-[120px]">
          <ol className="flex items-center space-x-2 py-[16px] md:py-[20px]">
            <li className="flex items-center gap-[4px] font-semibold">
              <Link to={role === 'publisher' ? '/book-store' : '/'} className="text-primary_500 hover:text-primary_600 transition-colors text-[14px] md:text-[18px]">
                {role === 'publisher' ? t('navigation.myBooks') : t('navigation.home')}
              </Link>
              <p className='text-primary_500'>/</p>
            </li>
            {showBookStore && (
              <li className="flex items-center gap-[4px]">
                <Link to="/book-store" className="text-primary_500 hover:text-primary_600 transition-colors text-[14px] md:text-[18px]">
                  {t('navigation.bookStore')}
                </Link>
                <p className='text-primary_500'>/</p>
              </li>
            )}
            {bookPaths.map((path, index) => {
              const bookId = path.split('/book/')[1];
              const bookTitle = bookNames[bookId] || `${t('common.loading')}`;
              const isLast = index === bookPaths.length - 1;

              return (
                <li key={bookId} className="flex items-center gap-[4px]">
                  {isLast ? (
                    <span className="text-[#435856]">{bookTitle}</span>
                  ) : (
                    <>
                      <Link 
                        to={`/book/${bookId}`} 
                        className="text-primary_500 hover:text-primary_600 transition-colors text-[14px] md:text-[18px]"
                        onClick={() => handleBookClick(bookId, index)}
                      >
                        {bookTitle}
                      </Link>
                      <p className='text-primary_500'>/</p>
                    </>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    );
  }

  // Special case for book store - show only My Books for publishers
  if (pathnames[0] === 'book-store' && role === 'publisher') {
    return (
      <nav aria-label="breadcrumb" className="bg-[rgba(73,156,151,0.24)] border-b-[1px] border-[rgba(73,156,151,0.24)]">
        <div className="px-[32px] md:px-[120px]">
          <ol className="flex items-center space-x-2 py-[16px] md:py-[20px]">
            <li className="flex items-center gap-[4px] font-semibold">
              <span className="text-primary_500 text-[14px] md:text-[18px]">{t('navigation.myBooks')}</span>
            </li>
          </ol>
        </div>
      </nav>
    );
  }

  // Special case for profile page - show selected page from sidebar
  if (pathnames[0] === 'profile') {
    const selectedPage = searchParams.get('page') || 'Profile';
    const getPageLabel = (page: string) => {
      switch (page) {
        case 'My Library':
          return t('navigation.myLibrary');
        case 'Purchase History':
          return t('navigation.purchaseHistory');
        case 'Profile':
          return t('navigation.profile');
        default:
          return page;
      }
    };

    return (
      <nav aria-label="breadcrumb" className="bg-[rgba(73,156,151,0.24)] border-b-[1px] border-[rgba(73,156,151,0.24)]">
        <div className="px-[32px] md:px-[120px]">
          <ol className="flex items-center space-x-2 py-[16px] md:py-[20px]">
            <li className="flex items-center gap-[4px] font-semibold">
              <Link to={role === 'publisher' ? '/book-store' : '/'} className="text-primary_500 hover:text-primary_600 transition-colors text-[14px] md:text-[18px]">
                {role === 'publisher' ? t('navigation.myBooks') : t('navigation.home')}
              </Link>
              <p className='text-primary_500'>/</p>
            </li>
            <li>
              <span className="text-[#435856]">{getPageLabel(selectedPage)}</span>
            </li>
          </ol>
        </div>
      </nav>
    );
  }

  const getCustomLabel = (path: string) => {
    const customLabels: { [key: string]: string } = {
      'book-store': t('navigation.bookStore'),
      'contact-us': t('navigation.contactUs'),
      'privacy-policies': t('navigation.privacyPolicies'),
      'terms-and-conditions': t('navigation.termsConditions'),
      'shopping-cart': t('navigation.shoppingCart'),
      'payment-complete': t('checkout.orderSummary'),
      'purchases': t('navigation.purchases'),
      'reports': t('navigation.reports'),
      'support': t('navigation.helpSupport'),
      'profile': t('navigation.profile'),
      'faqs': t('navigation.faqs'),
      'checkout': t('navigation.checkout')
    };

    return customLabels[path] || path;
  };

  const formatPathName = (path: string) => {
    const customLabel = getCustomLabel(path);
    if (customLabel !== path) {
      return customLabel;
    }

    // Special case for payment-complete page to show Order Summary / Complete
    if (path === 'paymentComplete') {
      return t('paymentComplete.title');
    }

    return path
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <nav aria-label="breadcrumb" className="bg-[rgba(73,156,151,0.24)] border-b-[1px] border-[rgba(73,156,151,0.24)]">
      <div className="px-[32px] md:px-[120px]">
        <ol className="flex items-center space-x-2 py-[16px] md:py-[20px]">
          <li className="flex items-center gap-[4px] font-semibold">
            <Link to={role === 'publisher' ? '/book-store' : '/'} className="text-primary_500 hover:text-primary_600 transition-colors text-[14px] md:text-[18px]">
              {role === 'publisher' ? t('navigation.myBooks') : t('navigation.home')}
            </Link>
            <p className='text-primary_500'>/</p>
          </li>
          {pathnames.map((value, index) => {
            // Stop showing breadcrumb items after paymentComplete
            if (pathnames.slice(0, index).includes('paymentComplete')) {
              return null;
            }

            const to = `/${pathnames.slice(0, index + 1).join('/')}`;
            const isLast = index === pathnames.length - 1 || value === 'paymentComplete';
            
            return (
              <li key={to} className="flex items-center">
                {isLast ? (
                  <span className="text-[#435856]">
                    {value === 'paymentComplete' ? (
                      <>
                        {t('checkout.orderSummary')} / {formatPathName(value)}
                      </>
                    ) : (
                      formatPathName(value)
                    )}
                  </span>
                ) : (
                  <>
                    <Link
                      to={to}
                      className="text-primary_500 hover:text-primary_600 transition-colors"
                    >
                      {formatPathName(value)}
                    </Link>
                    <p className='text-primary_500'>/</p>
                  </>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumb;
